/* Keyhole license-check (custom UI build) — input-popup + 6-layer protection.
 *
 * Server contract (unchanged from canonical):
 *   POST {KEYHOLE_ENDPOINT}  {license_key, shop_domain, theme_id}
 *        -> { valid:bool, token?:<ES256 JWT>, reason?:string }
 *   GET  {KEYHOLE_PUBKEY_URL} -> { raw_b64u:<raw ECDSA P-256 pubkey> }
 *   POST {KEYHOLE_HEARTBEAT} {token, shop_domain, theme_id} -> { revoked?:bool, reason? }
 *
 * Difference vs canonical: the locked popup contains a LICENSE KEY INPUT +
 * Activate button. Buyer types their key -> stored in localStorage -> live
 * re-validate against server -> unlock on success. No hardcoded server URL.
 */
(function () {
  "use strict";
  if (window.__KEYHOLE_CHECKED__) return;
  window.__KEYHOLE_CHECKED__ = true;

  // -------- CONFIG --------
  var endpoint = window.KEYHOLE_ENDPOINT;
  var heartbeatUrl = window.KEYHOLE_HEARTBEAT;
  var pubkeyUrl = window.KEYHOLE_PUBKEY_URL;
  var baseUrl = window.KEYHOLE_BASE || "";
  if (!endpoint) return;

  // Always prefer the Liquid-injected permanent domain: location.hostname is the
  // custom domain on live storefronts, which never matches the .myshopify.com
  // binding in the license DB (and also breaks token payload.s verification).
  var shopDomain = window.KEYHOLE_SHOP || (window.Shopify && window.Shopify.shop) || location.hostname;
  var themeId = window.KEYHOLE_THEME_ID || null;

  var LS = { tok: "kh_tok", exp: "kh_exp", hb: "kh_hb_last", pk: "kh_pubkey", key: "kh_user_key" };
  var CACHE_TTL_MS = 60 * 60 * 1000;
  var HEARTBEAT_INTERVAL_MS = 24 * 60 * 60 * 1000;
  var ANCHOR_POLL_MS = 1000;

  var COOKIE_KEY = "kh_license_key";
  function readCookieKey() {
    try {
      var m = document.cookie.match(new RegExp("(?:^|;\\s*)" + COOKIE_KEY + "=([^;]+)"));
      return m ? decodeURIComponent(m[1]) : "";
    } catch (e) { return ""; }
  }
  function readUserKey() {
    var k = "";
    try { k = localStorage.getItem(LS.key) || ""; } catch (e) {}
    return k || readCookieKey();
  }
  // Persist the buyer-entered key in BOTH localStorage and a 1-year cookie, so a
  // single popup activation survives storage clears and carries across sessions.
  function persistUserKey(key) {
    try { localStorage.setItem(LS.key, key); } catch (e) {}
    try {
      document.cookie = COOKIE_KEY + "=" + encodeURIComponent(key) +
        ";path=/;max-age=31536000;SameSite=Lax";
    } catch (e) {}
  }
  // Normalize: stray whitespace / lowercase from copy-paste must not invalidate a real key.
  function normKey(k) { return String(k || "").trim().toUpperCase(); }
  // buyer-entered key (localStorage/cookie) overrides the theme setting
  var licenseKey = normKey(readUserKey() || window.KEYHOLE_LICENSE_KEY) || null;

  var gateUnlocked = false;
  var verifiedUnlock = false; // true ONLY after an ECDSA-verified token
  var hardLocked = false;     // server explicitly rejected -> physically strip premium DOM
  try { window.__KH_GRANTED__ = false; } catch (e) {}

  // An unlock is granted only when a token passes offline ECDSA verify.
  function grantIfVerified(token) {
    return verifyToken(token).then(function (payload) {
      if (!payload) return false;
      verifiedUnlock = true;
      hardLocked = false;
      try { window.__KH_GRANTED__ = true; } catch (e) {}
      setCached(token);
      return true;
    });
  }

  // Entanglement: physically detach premium content from the DOM. This defeats a
  // pirate who removes the CSS fail-closed gates — the content is gone, not just hidden.
  // Only runs on an EXPLICIT server rejection (not transient network errors), so a
  // licensed shopper with a flaky connection is never nuked (cached token still unlocks).
  function stripPremiumContent() {
    try {
      var n = document.querySelectorAll(".premium-section, .premium-button");
      for (var i = 0; i < n.length; i++) if (n[i].parentNode) n[i].parentNode.removeChild(n[i]);
    } catch (e) {}
  }

  // -------- DEV-STORE BYPASS (only true local dev) --------
  // Do NOT bypass on myshopify.com / dev-/test- subdomains: a pirate could name a
  // store test-*.myshopify.com to unlock for free. Only localhost / *.local.
  function isDevStore() {
    var h = String(shopDomain || "").toLowerCase();
    if (!h) return false;
    if (h === "localhost" || h === "127.0.0.1") return true;
    if (h.indexOf(".local") !== -1) return true;
    return false;
  }

  function setMode(mode) {
    var body = document.body || document.documentElement;
    body.classList.remove("premium-unlocked", "free-mode");
    body.classList.add(mode);
  }

  // -------- BASE64URL --------
  function b64uToBytes(s) {
    s = String(s).replace(/-/g, "+").replace(/_/g, "/");
    while (s.length % 4) s += "=";
    var bin = atob(s), out = new Uint8Array(bin.length);
    for (var i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
  }

  // -------- PUBLIC KEY --------
  var pubkeyPromise = null;
  function getPubkey() {
    if (pubkeyPromise) return pubkeyPromise;
    var cached = null;
    try { cached = localStorage.getItem(LS.pk); } catch (e) {}
    if (cached) { pubkeyPromise = importPubkey(cached); return pubkeyPromise; }
    if (!pubkeyUrl) { pubkeyPromise = Promise.resolve(null); return pubkeyPromise; }
    pubkeyPromise = fetch(pubkeyUrl, { credentials: "omit", cache: "default" })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var raw = j && j.raw_b64u;
        if (!raw) return null;
        try { localStorage.setItem(LS.pk, raw); } catch (e) {}
        return importPubkey(raw);
      })
      .catch(function () { return null; });
    return pubkeyPromise;
  }
  function importPubkey(rawB64u) {
    if (!window.crypto || !window.crypto.subtle) return Promise.resolve(null);
    return window.crypto.subtle
      .importKey("raw", b64uToBytes(rawB64u), { name: "ECDSA", namedCurve: "P-256" }, false, ["verify"])
      .catch(function () { return null; });
  }

  // -------- TOKEN VERIFY (offline) --------
  function verifyToken(tok) {
    if (!tok || typeof tok !== "string") return Promise.resolve(null);
    var parts = tok.split(".");
    if (parts.length !== 3) return Promise.resolve(null);
    var payload;
    try { payload = JSON.parse(new TextDecoder().decode(b64uToBytes(parts[1]))); }
    catch (e) { return Promise.resolve(null); }
    if (!payload) return Promise.resolve(null);
    var sigShop = payload.s || payload.shop;
    if (sigShop && sigShop !== shopDomain) {
      try { console.warn("[Keyhole] token shop mismatch:", sigShop, "vs", shopDomain); } catch (e) {}
      return Promise.resolve(null);
    }
    var sigTheme = payload.p || payload.tid;
    if (themeId && sigTheme && sigTheme !== themeId) return Promise.resolve(null);
    if (!payload.exp || payload.exp * 1000 < Date.now()) return Promise.resolve(null);
    return getPubkey().then(function (key) {
      if (!key) return null;
      var data = new TextEncoder().encode(parts[0] + "." + parts[1]);
      return window.crypto.subtle
        .verify({ name: "ECDSA", hash: "SHA-256" }, key, b64uToBytes(parts[2]), data)
        .then(function (ok) { return ok ? payload : null; })
        .catch(function () { return null; });
    });
  }

  // -------- CACHE --------
  function getCached() {
    try {
      var tok = localStorage.getItem(LS.tok);
      var exp = parseInt(localStorage.getItem(LS.exp) || "0", 10);
      if (!tok || !exp || exp < Date.now()) return null;
      return tok;
    } catch (e) { return null; }
  }
  function setCached(tok) {
    try { localStorage.setItem(LS.tok, tok); localStorage.setItem(LS.exp, String(Date.now() + CACHE_TTL_MS)); } catch (e) {}
  }
  function clearCached() { try { localStorage.removeItem(LS.tok); localStorage.removeItem(LS.exp); } catch (e) {} }

  // -------- SHADOW-DOM POPUP WITH LICENSE INPUT --------
  var popupObserver = null;
  var GATE_ID = "kh-gate-root";

  function showPopup(reason) {
    if (gateUnlocked) return;
    if (document.getElementById(GATE_ID)) return;

    var host = document.createElement("div");
    host.id = GATE_ID;
    host.style.cssText = "position:fixed;inset:0;z-index:2147483647;pointer-events:auto;";
    var shadow = host.attachShadow ? host.attachShadow({ mode: "closed" }) : host;

    var safeShop = String(shopDomain).replace(/[<>&"]/g, "");
    var brand = String(window.KEYHOLE_BRAND || "").replace(/[<>&"]/g, "") || "Licensed";
    var reasonText = ({
      invalid: "Invalid license key.",
      expired: "This license has expired.",
      revoked: "This license was revoked.",
      bad_request: "",
      network: "Couldn't reach the license server.",
      tamper: "Theme integrity check failed.",
      unknown: ""
    })[reason] || "";

    // Pull the live theme palette so the dialog matches the theme's own marketing popup.
    var cs = getComputedStyle(document.documentElement);
    function pv(name, fb) { var v = cs.getPropertyValue(name).trim(); return v ? "rgb(" + v + ")" : fb; }
    function pvf(name, fb) { var v = cs.getPropertyValue(name).trim(); return v || fb; }
    var C_FG = pv("--color-foreground", "rgb(248,248,248)");
    var C_BG = pv("--color-background", "rgb(0,0,0)");
    var C_ACC = pv("--color-accent", "rgb(255,117,39)");
    var C_BORDER = pv("--color-border", "rgb(66,66,66)");
    var C_MUTED = pv("--color-muted-foreground", "rgba(248,248,248,.55)");
    var F_HEAD = pvf("--font-heading-family", '"UnifrakturCook", serif');
    var F_BODY = pvf("--font-body-family", '"Inter", system-ui, sans-serif');

    // Mirrors assets/marketing-popup.css (corner crosses, glitch gothic title, pill field).
    shadow.innerHTML =
      '<style>' +
      ':host{all:initial;}' +
      '*{box-sizing:border-box;margin:0;padding:0;font-family:' + F_BODY + ';}' +
      '.ovr{position:fixed;inset:0;z-index:1;background:rgba(0,0,0,.78);backdrop-filter:blur(4px);' +
      '-webkit-backdrop-filter:blur(4px);display:flex;align-items:center;justify-content:center;padding:1.5rem;}' +
      '.box{position:relative;width:100%;max-width:30rem;border:1px solid ' + C_BORDER + ';' +
      'background:' + C_BG + ';color:' + C_FG + ';}' +
      '.cross{position:absolute;width:12px;height:12px;border-top:1px solid ' + C_FG + ';' +
      'border-left:1px solid ' + C_FG + ';opacity:.6;}' +
      '.cross.tr{right:0;transform:scaleX(-1);}.cross.bl{bottom:0;transform:scaleY(-1);}' +
      '.cross.br{right:0;bottom:0;transform:scale(-1,-1);}' +
      '.inner{padding:3rem 2rem 2.25rem;text-align:center;}' +
      '@media(min-width:768px){.inner{padding:3.5rem 3rem 2.5rem;}}' +
      '.eyebrow{font-size:.625rem;text-transform:uppercase;letter-spacing:.4em;color:' + C_MUTED + ';}' +
      '.eyebrow i{display:inline-block;height:1px;width:1.75rem;vertical-align:middle;' +
      'background:' + C_FG + ';margin-right:.625rem;}' +
      '.t{position:relative;display:inline-block;font-family:' + F_HEAD + ';margin:.875rem 0 0;' +
      'font-size:3.25rem;line-height:.9;letter-spacing:-.02em;color:' + C_FG + ';}' +
      '@media(min-width:768px){.t{font-size:4rem;}}' +
      '.t::before,.t::after{content:attr(data-text);position:absolute;inset:0;pointer-events:none;}' +
      '.t::before{color:' + C_ACC + ';transform:translate(-2px,0);mix-blend-mode:screen;opacity:.65;}' +
      '.t::after{color:' + C_FG + ';transform:translate(2px,0);mix-blend-mode:screen;opacity:.55;}' +
      '.sub{max-width:22rem;margin:1rem auto 0;font-size:.8125rem;line-height:1.55;color:' + C_MUTED + ';}' +
      '.sub b{color:' + C_FG + ';font-weight:600;}' +
      '.form{margin-top:1.75rem;}' +
      '.field{display:flex;align-items:center;gap:.5rem;border:1px solid ' + C_BORDER + ';' +
      'border-radius:9999px;padding:.25rem .25rem .25rem 1.25rem;transition:border-color .2s;}' +
      '.field:focus-within{border-color:' + C_FG + ';}' +
      '.input{flex:1;min-width:0;background:transparent;border:0;outline:0;color:' + C_FG + ';' +
      'font-size:16px;font-family:' + F_BODY + ';letter-spacing:.02em;-webkit-appearance:none;appearance:none;}' +
      '.input::placeholder{color:' + C_MUTED + ';font-size:.75rem;letter-spacing:.04em;text-transform:uppercase;}' +
      '.submit{flex-shrink:0;display:inline-flex;align-items:center;gap:.5rem;border:0;cursor:pointer;' +
      'background:' + C_FG + ';color:' + C_BG + ';border-radius:9999px;padding:.625rem 1.125rem;' +
      'font-size:.6875rem;text-transform:uppercase;letter-spacing:.2em;transition:opacity .2s,transform .2s;}' +
      '.submit:hover{opacity:.85;}.submit:active{transform:scale(.96);}' +
      '.submit:disabled{opacity:.45;cursor:default;}' +
      '.arw{transition:transform .2s;}.submit:hover .arw{transform:translateX(3px);}' +
      '.msg{margin:.875rem 0 0;font-size:.75rem;letter-spacing:.05em;min-height:14px;}' +
      '.msg.err{color:' + C_ACC + ';}.msg.ok{color:' + C_FG + ';}' +
      '.fine{margin:1rem 0 0;font-size:.5625rem;text-transform:uppercase;letter-spacing:.24em;color:' + C_MUTED + ';}' +
      '</style>' +
      '<div class="ovr"><div class="box">' +
      '<span class="cross"></span><span class="cross tr"></span>' +
      '<span class="cross bl"></span><span class="cross br"></span>' +
      '<div class="inner">' +
      '<div class="eyebrow"><i></i>' + brand + '</div>' +
      '<h2 class="t" data-text="ACTIVATE">ACTIVATE</h2>' +
      '<p class="sub">Enter the license key from your purchase email to unlock this theme for <b>' + safeShop + '</b>.</p>' +
      '<div class="form"><div class="field">' +
      '<input id="khk" class="input" type="text" autocomplete="off" spellcheck="false" placeholder="License key" aria-label="License key" />' +
      '<button id="khb" class="submit"><span>Activate</span><span class="arw">&#8594;</span></button>' +
      '</div>' +
      '<p class="msg' + (reasonText ? " err" : "") + '" id="khm">' + reasonText + '</p>' +
      '<p class="fine">Single-store license &middot; Non-transferable</p>' +
      '</div></div></div></div>';

    (document.body || document.documentElement).appendChild(host);

    var input = shadow.getElementById("khk");
    var btn = shadow.getElementById("khb");
    var msg = shadow.getElementById("khm");
    if (input) input.value = readUserKey();

    function submit() {
      var key = normKey(input.value);
      if (!key) { msg.className = "msg err"; msg.textContent = "Enter a license key."; return; }
      persistUserKey(key);
      licenseKey = key;
      btn.disabled = true;
      msg.className = "msg";
      msg.textContent = "Checking…";
      checkServer().then(function (j) {
        if (j && j.valid === true && j.token) {
          // must pass ECDSA verify — a spoofed {valid:true} without a signed token fails here
          grantIfVerified(j.token).then(function (ok) {
            if (ok) {
              maybeHeartbeat(j.token);
              msg.className = "msg ok";
              msg.textContent = "Activated. Unlocking…";
              setTimeout(unlock, 500);
            } else {
              btn.disabled = false;
              msg.className = "msg err";
              msg.textContent = "Invalid license token.";
            }
          });
        } else {
          try { console.warn("[Keyhole] activation rejected:", (j && j.reason) || "unknown", { shop: shopDomain, theme_id: themeId, response: j }); } catch (e) {}
          btn.disabled = false;
          msg.className = "msg err";
          msg.textContent = ({
            invalid: "Invalid license key.",
            expired: "This license has expired.",
            revoked: "This license was revoked.",
            network: "Couldn't reach the license server."
          })[j && j.reason] || "License rejected. Check the key or Theme ID.";
        }
      });
    }
    if (btn) btn.addEventListener("click", submit);
    if (input) input.addEventListener("keydown", function (e) { if (e.key === "Enter") submit(); });
  }

  function removePopup() {
    var h = document.getElementById(GATE_ID);
    if (h && h.parentNode) h.parentNode.removeChild(h);
  }

  function lock(reason) {
    setMode("free-mode");
    // PHYSICALLY strip content ONLY on genuine tampering (forged class / spoofed
    // endpoint / removed anchor). A wrong/expired/revoked key just shows the popup and
    // stays fully recoverable — a buyer who typos their key must NOT see a broken theme,
    // and entering the correct key right after must reveal everything (content still in DOM).
    if (reason === "tamper") {
      hardLocked = true;
      stripPremiumContent();
    }
    showPopup(reason);
    if (popupObserver) return;
    popupObserver = new MutationObserver(function () {
      if (!gateUnlocked && !document.getElementById(GATE_ID)) showPopup(reason);
    });
    popupObserver.observe(document.documentElement, { childList: true, subtree: true });
  }

  function unlock() {
    gateUnlocked = true;
    verifiedUnlock = true;
    hardLocked = false;
    try { window.__KH_GRANTED__ = true; } catch (e) {}
    if (popupObserver) { popupObserver.disconnect(); popupObserver = null; }
    removePopup();
    setMode("premium-unlocked");
  }

  // -------- CLASS-INTEGRITY WATCHDOG --------
  // Strips a forged/hardcoded `premium-unlocked` (added by a pirate in Liquid or via
  // JS) unless THIS script granted it after an ECDSA-verified token. If the script is
  // deleted instead, the inline fail-closed gate keeps premium hidden.
  function startWatchdog() {
    function enforce() {
      if (verifiedUnlock) return;
      var b = document.body;
      if (b && b.classList.contains("premium-unlocked")) {
        b.classList.remove("premium-unlocked");
        b.classList.add("free-mode");
        hardLocked = true;
        showPopup("tamper");
      }
      if (hardLocked) stripPremiumContent();
    }
    try {
      var mo = new MutationObserver(enforce);
      if (document.body) mo.observe(document.body, { attributes: true, attributeFilter: ["class"] });
    } catch (e) {}
    setInterval(enforce, 800);
    enforce();
  }

  // -------- CROSS-GUARD (4 anchors across 4 files) --------
  var reFetched = false;
  function anchorsIntact() {
    return !!(window.__kh_core__ && window.__kh_guard_h__ && window.__kh_guard_f__ && window.__kh_guard_c__);
  }
  function startCrossGuard() {
    setInterval(function () {
      if (gateUnlocked) return;
      if (anchorsIntact() || reFetched) return;
      reFetched = true;
      var s = document.createElement("script");
      s.src = (baseUrl || "") + "/license-check.js?cb=" + Date.now();
      s.defer = true;
      (document.body || document.documentElement).appendChild(s);
      lock("tamper");
    }, ANCHOR_POLL_MS);
  }

  // -------- SERVER CHECK --------
  function checkServer() {
    var controller = "AbortController" in window ? new AbortController() : null;
    var timeoutId = setTimeout(function () { if (controller) controller.abort(); }, 6000);
    return fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ license_key: licenseKey || "", shop_domain: shopDomain, theme_id: themeId }),
      signal: controller ? controller.signal : undefined,
      credentials: "omit",
      cache: "no-store"
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (j) { clearTimeout(timeoutId); return j || {}; })
      .catch(function () { clearTimeout(timeoutId); return { valid: false, reason: "network" }; });
  }

  // -------- HEARTBEAT --------
  function maybeHeartbeat(token) {
    if (!heartbeatUrl || !token) return;
    var last = 0;
    try { last = parseInt(localStorage.getItem(LS.hb) || "0", 10); } catch (e) {}
    if (Date.now() - last < HEARTBEAT_INTERVAL_MS) return;
    try { localStorage.setItem(LS.hb, String(Date.now())); } catch (e) {}
    fetch(heartbeatUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: token, shop_domain: shopDomain, theme_id: themeId }),
      credentials: "omit", cache: "no-store", keepalive: true
    })
      .then(function (r) { return r.json().catch(function () { return {}; }); })
      .then(function (j) { if (j && j.revoked) { clearCached(); lock(j.reason || "revoked"); } })
      .catch(function () {});
  }

  // -------- MAIN --------
  function init() {
    if (isDevStore()) { verifiedUnlock = true; try { window.__KH_GRANTED__ = true; } catch (e) {} setMode("premium-unlocked"); return; }
    startWatchdog();
    startCrossGuard();
    var cached = getCached();
    if (cached) {
      grantIfVerified(cached).then(function (ok) {
        // unlock() (not bare setMode) — it sets gateUnlocked so the cross-guard/
        // watchdog never false-fires a tamper popup on pages missing an anchor.
        if (ok) { unlock(); maybeHeartbeat(cached); return; }
        clearCached(); slowPath();
      });
      return;
    }
    slowPath();
  }

  function slowPath() {
    // No local key: still ask the server with shop_domain only — the backend
    // auto-binds the domain on the first successful activation, so a buyer's new
    // device/incognito unlocks without re-entering the key. Popup only shows if
    // the domain isn't bound yet (first-ever activation) or the server rejects.
    checkServer().then(function (j) {
      if (j && j.valid === true && j.token) {
        // must pass ECDSA verify — spoofing {valid:true} from a fake endpoint fails here
        grantIfVerified(j.token).then(function (ok) {
          if (ok) {
            if (licenseKey) persistUserKey(licenseKey); // refresh cookie/localStorage copies
            unlock(); maybeHeartbeat(j.token);
          }
          else {
            try { console.warn("[Keyhole] valid:true but token failed verification", { shop: shopDomain, theme_id: themeId }); } catch (e) {}
            lock("tamper"); // server said valid:true but token fails ECDSA = forged endpoint, not a real buyer
          }
        });
        return;
      }
      try { console.warn("[Keyhole] license rejected:", (j && j.reason) || "invalid", { shop: shopDomain, theme_id: themeId, response: j }); } catch (e) {}
      // No key entered yet -> neutral ACTIVATE popup, not an error message.
      lock(licenseKey ? ((j && j.reason) || "invalid") : "unknown");
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
